import React, { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';

const CandlestickChart = ({ coinSymbol = 'BTC', height = 240 }) => {
  const chartContainerRef = useRef();

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: 'solid', color: '#f8f9fa' },
        textColor: '#6c757d',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: '#e9ecef' },
        horzLines: { color: '#e9ecef' },
      },
      rightPriceScale: {
        borderColor: '#dee2e6',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderColor: '#dee2e6',
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        vertLine: {
          color: '#9e9e9e',
          width: 0.5,
          style: 1,
          labelBackgroundColor: '#6c757d',
        },
        horzLine: {
          color: '#9e9e9e',
          width: 0.5,
          style: 1,
          labelBackgroundColor: '#6c757d',
        },
      },
    });

    // Generate realistic candlestick data
    const generateCandlestickData = () => {
      const data = [];
      let basePrice = 100;
      const now = Math.floor(Date.now() / 1000);

      for (let i = 60; i >= 0; i--) {
        const time = now - i * 300; // 5-minute intervals

        // Random price movement
        const trend = (Math.random() - 0.5) * 2;
        const volatility = Math.random() * 3;

        const open = basePrice;
        const close = open + trend * volatility;
        const high = Math.max(open, close) + Math.random() * 2;
        const low = Math.min(open, close) - Math.random() * 2;

        data.push({
          time,
          open: parseFloat(open.toFixed(2)),
          high: parseFloat(high.toFixed(2)),
          low: parseFloat(low.toFixed(2)),
          close: parseFloat(close.toFixed(2)),
        });

        basePrice = close;
      }
      return data;
    };

    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#10b981',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
      priceLineVisible: false,
      lastValueVisible: true,
    });

    candlestickSeries.setData(generateCandlestickData());

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);
    chart.timeScale().fitContent();

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [coinSymbol, height]);

  return (
    <div>
      <div className='d-flex justify-content-center gap-3 mb-2'>
        <small className='text-success'>● Profit</small>
        <small className='text-danger'>● Loss</small>
      </div>
      <div
        ref={chartContainerRef}
        style={{
          width: '100%',
          height: `${height}px`,
          borderRadius: '6px',
          overflow: 'hidden',
          border: '1px solid #e9ecef',
        }}
      />
    </div>
  );
};

export default CandlestickChart;
